/**
 * Extract customer name from the conversation transcript.
 * Looks for the pattern: bot asks for name → user responds with a short name.
 * Also catches bot addressing the user by name (e.g., "נעים להכיר, רונן").
 */
export function extractCustomerNameFromTranscript(transcript: { role: string; content: string }[]): string | undefined {
  // Strategy 1: Find user response right after bot asks for name
  const nameAskPatterns = [
    /מה (ה)?שם/i,           // "מה השם שלך" / "מה שמך"
    /עם מי.*הכבוד/i,       // "עם מי יש לי את הכבוד"
    /מי מדבר/i,             // "מי מדבר"
    /מי את[הן]?/i,          // "מי אתה"
    /איך קוראים לך/i,       // "איך קוראים לך"
    /what.*your name/i,      // English fallback
    /who am i speaking/i,
    /may i.*your name/i,
  ]

  for (let i = 0; i < transcript.length - 1; i++) {
    const msg = transcript[i]
    const nextMsg = transcript[i + 1]

    // Bot asked for name, next message is user's response
    if (msg.role === 'assistant' && nextMsg.role === 'user') {
      const botAskedForName = nameAskPatterns.some(p => p.test(msg.content))
      if (botAskedForName) {
        const response = nextMsg.content.trim()
        // Name responses are typically short (1-4 words), no numbers
        if (response.length > 0 && response.length <= 40 && !/\d/.test(response)) {
          // Clean common prefixes
          const cleaned = response
            .replace(/^(אני|שמי|קוראים לי|השם שלי|my name is|i'm|i am)\s*/i, '')
            .replace(/[.!?,]+$/g, '') // Remove trailing punctuation
            .trim()
          if (cleaned.length > 0 && cleaned.split(/\s+/).length <= 4) {
            return cleaned
          }
        }
      }
    }
  }

  // Strategy 2: Find bot using the customer's name in conversation
  // e.g., "נעים להכיר, רונן" or "שמח להכיר אותך רונן"
  const nameUsagePatterns = [
    /נעים להכיר,?\s*(.+?)(?:[.!,]|$)/i,
    /שמח[הא]? להכיר.*?,?\s*(.+?)(?:[.!,]|$)/i,
    /היי\s+(.+?),/i,                           // "היי רונן,"
    /nice to meet you,?\s*(.+?)(?:[.!,]|$)/i,
  ]

  for (const msg of transcript) {
    if (msg.role === 'assistant') {
      for (const pattern of nameUsagePatterns) {
        const match = msg.content.match(pattern)
        if (match?.[1]) {
          const name = match[1].trim().replace(/[.!?,]+$/g, '')
          // Sanity check: name should be short and not contain common words
          if (name.length > 0 && name.length <= 30 && name.split(/\s+/).length <= 3 && !/\d/.test(name)) {
            return name
          }
        }
      }
    }
  }

  return undefined
}
